// biome-ignore-all lint/suspicious/noArrayIndexKey: result groups and testNames can repeat; composite keys use index as tiebreaker
import { cx } from '@emotion/css';
import { Empty } from 'antd';
import React, { useMemo, useRef } from 'react';
import type { LabResultResponse } from '../../../../../../generated/openapi/befunde-api';
import { useLabResultViewStore } from '../../../../../../store/useLabResultViewStore.ts';
import { filterLabResultObservationResultGroups } from '../../../../../../utils/matcher.ts';
import { GenderIcon } from '../../../header/BefundDetailsHeader.tsx';
import { useGridClassNames } from '../OrderResult.styles.ts';
import { ObservationResultRenderer } from './ObservationResultRenderer';

export const ObservationResultsRenderer: React.FC<{
  result: LabResultResponse;
  deemphasizePathological: boolean;
}> = ({ result, deemphasizePathological }) => {
  const tokens = useLabResultViewStore(state => state.tokens);
  const valuesFilter = useLabResultViewStore(state => state.valuesFilter());
  const styles = useGridClassNames();

  const headerRef = useRef(null);
  const groups = useMemo(
    () => filterLabResultObservationResultGroups(result.observationResults, valuesFilter, tokens),
    [result.observationResults, valuesFilter, tokens]
  );

  if (result.observationResults.length === 0) {
    if (result.labResultAttachments.length > 0) {
      return null;
    }
    return <Empty description="Keine Werte" />;
  }

  if (groups.length === 0) {
    return <Empty description="Keine Untersuchung für angegeben Filter gefunden" />;
  }

  return groups.map((group, groupIndex) => {
    return (
      <React.Fragment key={`${group.groupName}${groupIndex}`}>
        <div ref={headerRef} className={cx(styles.gridClassName(groupIndex), styles.stickyClassName(groupIndex, true))}>
          <h3 className={styles.headerClassName}>{group.groupName}</h3>
        </div>
        <div className={styles.stickyClassName(groupIndex, false)}>
          <div className={cx(styles.gridClassName(groupIndex), styles.headerBorderClassName)}>
            <div className={cx(styles.accreditationClassName, styles.subheaderClassName)}></div>
            <div className={cx(styles.parameterClassName, styles.subheaderClassName)}>Untersuchung</div>
            <div className={cx(styles.resultClassName, styles.subheaderClassName)}>Ergebnis</div>
            <div className={cx(styles.pathologicalClassName, styles.subheaderClassName)}></div>
            <div className={cx(styles.unitClassName, styles.subheaderClassName)}>Einheit</div>
            <div className={cx(styles.rangeClassName, styles.subheaderClassName)}>
              Normbereich
              <GenderIcon gender={result.patient.gender} className={styles.genderIcon} />
            </div>
            <div className={cx(styles.graphicClassName, styles.subheaderClassName)}></div>
            <div className={cx(styles.popupGraphicClassName, styles.subheaderClassName)}></div>
          </div>
          {group.results.map((result, index) => (
            <ObservationResultRenderer
              key={`${result.testName}${index}`}
              index={groupIndex}
              result={result}
              deemphasizePathological={deemphasizePathological}
            />
          ))}
        </div>
      </React.Fragment>
    );
  });
};
